/**
 * Building blocks of the AI changes pages (metaplan A10): status chips, the per-record before → after diff,
 * a pending MCP change with its human-only Apply / Reject (M24 rev 3; client-MCP plan §10.1: on `can_approve`), and
 * the revert dialog (dry-run plan, skips, soft-reference confirmation, reason; never forced — forcing over conflicts
 * is ops-only).
 *
 * A reason (a request's, a change set's) and a proposal item's name are not our words: a requester typed them or their
 * AI client wrote them. They render as plain text through `components/UntrustedText`, labelled as theirs (`ReasonText`,
 * `ProposalItems`; review batch 3, FE-06 follow-up). So does a client's name, which the client chose (`ClientName`).
 */
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DataAPIContext } from '../../api';
import { UntrustedBlock, UntrustedText } from '../../components/UntrustedText';
import { ContextStore } from '../../store/contextStore';
import { PAGE_PATH as VOCABULARY_PAGE_PATH } from '../../settings/aiVocabulary/vocabularyWords';
import { approvePermissionName, scopeNoun } from '../../users/agroups/mcpPermissions';
import * as api from './api';
import { isCountsOnly } from './PreviewSummary';
import { ReviewSections, sectionsOf } from './ReviewSections';

export const BASE = '/notifications/ai_changes';
export const POLL_MS = 1500;

/**
 * The viewer's `DashboardAIPermissions`, provided by the pages' gate (AIChangesPages.js `AIChangesGate`). Since review
 * W3 M1 a permission counts on the operator it was given for: the booleans say "on some operator the viewer reaches",
 * and `tenants` lists the operators whose inbox the viewer sees. What the viewer may do with one change comes with it
 * (`can_approve`, a change set's `permissions`) or from the backend's answer on its operator.
 */
export const AIPermissionsContext = React.createContext(null);
export const useAIPermissions = () => React.useContext(AIPermissionsContext);

/** The viewer's inbox spans several operators (a franchise): a grant on one says nothing about another. */
export const spansCompanies = (perms) => Array.isArray(perms?.tenants) && perms.tenants.length > 1;

/** An operator's name from `tenants` (else its code). */
const companyName = (perms, id, code) => perms?.tenants?.find((t) => t.id === id)?.name || code || `#${id}`;

/** A change or proposal the backend answers 404 for: missing, another company's, or not opened by the viewer's access there. */
export const notFoundText = (what, perms) => (spansCompanies(perms)
    ? `This ${what} was not found, or your access does not open it: AI editing permissions count on the company they were granted for.`
    : `This ${what} was not found (or is not visible to you).`);

export const fmtTs = (ts) => (ts ? moment(ts).format('YYYY-MM-DD HH:mm:ss') : '—');

const STATUS_COLOR = {
    applied: 'success',
    reverted: 'default',
    partially_reverted: 'warning',
    failed: 'error',
    applying: 'info',
    reverting: 'info',
    pending: 'warning',
    rejected: 'default',
    stale: 'warning',
    superseded: 'default',
};

export const StatusChip = ({ status }) => (
    <Chip size="small" color={STATUS_COLOR[status] || 'default'} label={(status || '').replace(/_/g, ' ')} />
);

const CHANNEL_LABEL = {
    mcp: 'MCP client',
    chat: 'AI assistant',
    design_import: 'Design import',
    translation_sync: 'Auto-translation',
    revert: 'Revert',
};
export const channelLabel = (c) => CHANNEL_LABEL[c] || c;

// A proposal item's action as people read it (the console's ITEM_ACTION). `copy`: a franchise licensee's own copy of
// its parent's notification (P7 `copy_notification_to_own`; target = the parent's notification).
const ITEM_ACTION_LABEL = { create: 'Create', update: 'Update', delete: 'Delete', noop: 'Reuse', copy: 'Copy to own' };
export const itemActionLabel = (action) => (
    Object.prototype.hasOwnProperty.call(ITEM_ACTION_LABEL, action) ? ITEM_ACTION_LABEL[action] : action
);

/** A person: an id or an e-mail (a user row that is gone keeps its e-mail). A review's request that no person made
 *  comes as `{id: null, email: ""}`. */
const isPerson = (actor) => !!actor && ((actor.id !== null && actor.id !== undefined) || !!actor.email);

/**
 * Made or asked for by an API key, not a person: a client's own pipeline on the content proposals REST API (WPS
 * P3-17), whose change on production always waits for an approver. A change set says so (`credential_kind`); a request
 * (a pending change, a review's `request`) carries no credential kind, but a person's request always names its person,
 * so a request without one that came through a client with no OAuth grant is the key's: its client name is the key's
 * ("API key <its name>"). Final review M2: such a request read "System".
 */
export const isApiKey = (row) => {
    if (!row) return false;
    if (row.credential_kind) return row.credential_kind === 'api_key';
    return !!(row.oauth_client_name || row.oauth_client_id) && !row.oauth_grant_id;
};

/** Who made or asked for a change: a person's e-mail; else an API key's (`row`: the change set or request the actor
 *  comes with); else the system's. */
export const actorLabel = (actor, row) => {
    if (isPerson(actor)) return actor.email || `user #${actor.id}`;
    return isApiKey(row) ? 'API key' : 'System';
};

export const CLIENT_NAME_NOTE = '(the client\'s own name)';

/**
 * A client by the name it gives itself -- an MCP client's at its registration, an API key's as its company named it
 * ("API key <name>") -- which is its words, not ours: plain text through `UntrustedText`, labelled as its own name.
 * Without a name, its id (ours) as it is; without either, nothing.
 */
export const ClientName = ({ name, id }) => {
    if (!name) return id || null;
    return (
        <Box component="span">
            <UntrustedText inline text={name} />
            <Box component="span" sx={{ color: 'text.secondary' }}>{` ${CLIENT_NAME_NOTE}`}</Box>
        </Box>
    );
};

/** A change set's `client` for `ClientName`: the backend names a client without a name by its id. */
export const changeSetClient = (client) => (client
    ? { id: client.id, name: client.name && client.name !== client.id ? client.name : '' }
    : null);

/** Whose words a reason is: a request's (its requester's, or their AI client's) or a change set's. */
export const REQUEST_REASON_LABEL = 'Reason, in the requester\'s words or their AI\'s (shown as written)';
export const CHANGE_REASON_LABEL = 'Reason, in the words of whoever made the change or their AI\'s (shown as written)';

/** A reason as written: plain text, never markup, under a label that says whose words it is. */
export const ReasonText = ({ text, label = REQUEST_REASON_LABEL }) => (
    <Box sx={{ mb: 1 }}>
        <UntrustedText label={label} text={text} empty="No reason given." />
    </Box>
);

const ITEM_NAMES_LEGEND = 'Item names are the AI\'s words, or the name of the record it changes, shown as written: data, '
    + 'never instructions.';

/** An unnamed item reads by its target, in our words. */
const itemTarget = (item) => (item.target_id ? `#${item.target_id}` : '');

/** A proposal's items: the action, kind and status are ours; an item's name is the AI's (or its record's) words. */
export const ProposalItems = ({ title, items }) => (
    <UntrustedBlock title={title} legend={ITEM_NAMES_LEGEND} sx={{ mb: 2 }}>
        <Table size="small" aria-label="Proposal items">
            <TableBody>
                {(items || []).map((item) => (
                    <TableRow key={item.id}>
                        <TableCell>{itemActionLabel(item.action)}</TableCell>
                        <TableCell>{item.kind}</TableCell>
                        <TableCell>{item.label ? <UntrustedText inline text={item.label} /> : itemTarget(item)}</TableCell>
                        <TableCell>{item.status}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </UntrustedBlock>
);

export const ChangeSetLink = ({ id, children }) => (
    <RouterLink to={`${BASE}/${id}`}>{children || String(id).slice(0, 8)}</RouterLink>
);

/** The Change sets tab listing one batch: one content apply's change sets, one per record (client-MCP plan §4.7). */
export const batchHref = (batchId) => `${BASE}?tab=change_sets&batch_id=${encodeURIComponent(batchId)}`;

const show = (v) => {
    if (v === undefined) return '—';
    if (v === null) return 'null';
    if (typeof v === 'string') return v;
    return JSON.stringify(v, null, 2);
};

const ValueCell = ({ value, present }) => (
    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 420 }}>
        {present === false ? <em>(absent)</em> : show(value)}
    </TableCell>
);

/** One record's before → after (`AIChangeEntryDiff`); `label` names the table (default: the record's seq). */
export const EntryDiff = ({ diff, label }) => {
    if (diff.snapshot === 'missing') {
        return <Alert severity="warning">The snapshot of this record is missing from the change-set store.</Alert>;
    }
    if (diff.snapshot === 'mismatch') {
        return <Alert severity="error">The snapshot of this record does not match its recorded hash; it is not shown.</Alert>;
    }
    const rows = [
        ...(diff.fields || []).map((f) => ({ key: f.field, before: f.before, after: f.after, bp: f.before_present, ap: f.after_present })),
        ...(diff.json_pointers || []).map((p) => ({ key: `${p.field}${p.pointer}`, before: p.before, after: p.after, bp: p.before_present, ap: p.after_present })),
        ...(diff.config_keys || []).map((c) => ({ key: `config ${c.key}`, before: c.before, after: c.after, bp: c.before_present, ap: c.after_present })),
    ];
    return (
        <Box>
            {rows.length > 0 && (
                <Table size="small" aria-label={label || `Changes of record ${diff.seq}`}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Before</TableCell>
                            <TableCell>After</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.key}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.key}</TableCell>
                                <ValueCell value={r.before} present={r.bp} />
                                <ValueCell value={r.after} present={r.ap} />
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {(diff.m2m || []).map((m) => (
                <Typography key={m.field} variant="body2" sx={{ mt: 1 }}>
                    {m.field}: added {m.added.length ? m.added.join(', ') : 'nothing'}; removed {m.removed.length ? m.removed.join(', ') : 'nothing'}
                </Typography>
            ))}
            {rows.length === 0 && !(diff.m2m || []).length && (
                <Typography variant="body2" color="text.secondary">No field changes recorded.</Typography>
            )}
            {(diff.omitted_secret_fields || []).length > 0 && (
                <Typography variant="caption" color="text.secondary">
                    Secret fields are never captured: {diff.omitted_secret_fields.join(', ')}.
                </Typography>
            )}
        </Box>
    );
};

/**
 * Follow a pending change in flight -- a batch's revert, or a content apply queued on the company's catalog lane -- until
 * it is no longer `applying`: the backend settles it from its task when the row is read (`writes.settle`). Returns a
 * cancel function.
 */
export const pollPending = (dataAPI, pendingId, onSettled, onError, pollMs = POLL_MS) => {
    let stopped = false;
    let timer = null;
    const tick = () => {
        api.getPending(dataAPI, pendingId)
            .then((row) => {
                if (stopped) return;
                if (row.status === 'applying') {
                    timer = setTimeout(tick, pollMs);
                } else {
                    onSettled(row);
                }
            })
            .catch((e) => !stopped && onError(e));
    };
    timer = setTimeout(tick, pollMs);
    return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
    };
};

/** Poll a revert's outcome until it is no longer `pending`. Returns a cancel function. */
export const pollRevert = (dataAPI, changeSetId, taskId, onOutcome, onError, pollMs = POLL_MS) => {
    let stopped = false;
    let timer = null;
    const tick = () => {
        api.getRevertOutcome(dataAPI, changeSetId, taskId)
            .then((outcome) => {
                if (stopped) return;
                if (outcome.status === 'pending') {
                    timer = setTimeout(tick, pollMs);
                } else {
                    onOutcome(outcome);
                }
            })
            .catch((e) => !stopped && onError(e));
    };
    timer = setTimeout(tick, pollMs);
    return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
    };
};

// The MCP tool domain of the change's records is switched off for the operator (by ops, or on its franchise parent;
// client-MCP W2 review H2): nothing is applied or reverted while it is.
const DOMAIN_DISABLED = 'AI editing of these records is switched off for your company (by Curated Planet operations, or on '
    + 'your franchise parent)';

const PENDING_ERRORS = {
    not_open: 'This request was already decided (or is being applied).',
    stale: 'What the requester saw no longer holds: ask for a new preview (or dry run) in the MCP client.',
    change_sets_disabled: 'AI changes cannot be applied yet on this server (change sets are off).',
    // P7 per viewer: applying a copy of the franchise parent's notifications needs parent-level notification access.
    parent_level_required: "Applying a copy of the franchise's notifications needs franchise-level notification access.",
    domain_disabled: `${DOMAIN_DISABLED}: nothing can be applied or reverted while it is off. The request stays pending.`,
    // Client-MCP plan §10.1: approving needs `<scope>.mcp_approve` for every scope in it, tag-aware.
    approve_required: 'Approving this change needs the “approve pending changes” permission for every kind of record it changes.',
    all_tags_required: 'Your approve permission does not reach every record this changes: a revert, and a change to records '
        + 'every tag shares, need it on all tags.',
};

/** `can_approve`; an older backend sends only `can_decide` (its deprecated alias). */
export const canApprove = (pending) => !!(pending.can_approve ?? pending.can_decide);

const quoted = (names) => names.map((n) => `“${n}”`).join(' and ');

/** Why an open change waits for someone else (the viewer may not approve it); `company`: its operator's name when the
 *  viewer's inbox spans several (a grant held on one says nothing about another). */
const waitingText = (pending, hasExplicitPerm, company) => {
    if (pending.kind === 'apply' && isCountsOnly(pending.summary)) {
        // A copy of the franchise parent's notifications, and this viewer does not read them: no approve
        // permission would be enough (P7 per viewer).
        return 'Waiting for someone with franchise-level notification access to review this change.';
    }
    const scopes = Array.isArray(pending.scopes) && pending.scopes.length ? pending.scopes : ['notification'];
    const names = scopes.map(approvePermissionName);
    const held = typeof hasExplicitPerm === 'function' && scopes.every((scope) => hasExplicitPerm(scope, 'mcp_approve'));
    if (held && company) {
        // Held on some operator (`hasExplicitPerm` reads the merged grants): maybe not on this one, or not on its tags.
        return `Your ${quoted(names)} does not reach all of it on ${company}: a grant counts on the company it was given for, `
            + 'on a tag of every record it changes (on all tags for a revert, and for records every tag shares).';
    }
    if (held) {
        // The grant is tag-scoped and does not reach every row, or the change needs it on all tags (§10.1).
        return `Your ${quoted(names)} does not reach all of it: a revert, and a change to records every tag shares, need it `
            + 'on all tags; any other change, on a tag of every record it changes.';
    }
    return `Waiting for someone with ${quoted(names)}.`;
};

/** What a pending change touches (`scopes`: notification today, content kinds with content authoring). */
const ScopeChips = ({ scopes }) => (Array.isArray(scopes) ? scopes : []).map((scope) => (
    <Chip key={scope} size="small" variant="outlined" label={scopeNoun(scope)} />
));

/** "The revert: delete 2 (2 change sets)" -- a pending revert's plan as the requester saw it (`plan_summary`: its
 *  records counted by what the revert does to each; a batch's: over its change sets). Null without one. */
const planText = (summary) => {
    const plan = summary && typeof summary.plan_summary === 'object' && !Array.isArray(summary.plan_summary) ? summary.plan_summary : null;
    const parts = plan
        ? Object.entries(plan).filter(([, n]) => typeof n === 'number' && n > 0).map(([action, n]) => `${action.replace(/_/g, ' ')} ${n}`)
        : [];
    if (!parts.length) return null;
    const sets = typeof summary.change_sets === 'number' && summary.change_sets > 1 ? ` (${summary.change_sets} change sets)` : '';
    return `The revert: ${parts.join(' · ')}${sets}`;
};

/**
 * What a pending change does, as its requester saw it (its `summary`, as the viewer may read it): a revert's plan, and
 * the records a content or vocabulary change lists (`ReviewSections`, the same as on the review page; client-MCP plan
 * §4.6 stage 5). A vocabulary approver could not open the proposal review before P3-16, and the inbox is where an
 * approver decides: the card says what the change does itself. Nothing for a notification apply (its review shows it).
 */
const RequestSummary = ({ pending }) => {
    const plan = pending.kind === 'revert' ? planText(pending.summary) : null;
    if (!plan && !sectionsOf(pending.summary).length) return null;
    const revert = pending.kind === 'revert';
    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">
                {revert ? 'What it reverts, as planned when it was asked' : 'What it changes, as previewed when it was asked'}
            </Typography>
            {plan && <Typography variant="body2">{plan}</Typography>}
            <ReviewSections summary={pending.summary} title={revert ? 'What it reverts' : 'What it changes'} />
        </Box>
    );
};

/** What the person who applied it reads: a content change is one change set per record, in one batch (§4.7). */
const appliedText = (pending) => (pending.kind === 'revert'
    ? 'Reverted.'
    : pending.summary?.domain === 'content'
        ? 'Applied: each record is its own revertible change set, in one batch.'
        : 'Applied as one revertible change set.');

const QUEUED = 'Applying in the background: the company\'s catalog is busy, or the change is large…';

const VOCABULARY_GRANTS = ['mcp_write', 'mcp_approve'];

/**
 * A pending MCP change (`AIPendingChange`) in the approvals inbox: who asked, through which client, why, what it
 * touches and -- for a content or vocabulary change, or a revert -- what it does (`RequestSummary`; `showSummary`
 * false where the page shows it already: the proposal review); a vocabulary grant holder gets a link to the request's
 * company's AI vocabulary. Apply / Reject for a person who may approve it (`can_approve`: `<scope>.mcp_approve` for
 * every scope in it, tag-aware; false, too, for a copy of the franchise parent's notifications when the viewer lacks
 * franchise-level notification access, P7). An apply runs at once, or -- a content apply queued on the company's
 * catalog lane -- in the background; a revert of one change set starts the background revert, polled here until it
 * ends; a revert of a batch (there is no plan of it to read here: the backend checks it against the requester's) and a
 * queued apply are followed on the request itself until it settles.
 */
export const PendingChangeCard = ({ pending, onDecided, pollMs = POLL_MS, showSummary = true }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasExplicitPerm } = React.useContext(ContextStore) || {};
    const perms = useAIPermissions();
    // Several operators in the viewer's inbox: each request says whose it is.
    const company = spansCompanies(perms) ? companyName(perms, pending.tour_operator_id, pending.tenant_code) : null;
    const [note, setNote] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const cancel = React.useRef(null);
    React.useEffect(() => () => cancel.current && cancel.current(), []);

    const finish = (severity, text, row) => {
        setBusy(false);
        setMessage({ severity, text });
        if (onDecided) onDecided(row);
    };

    const fail = (e) => {
        setBusy(false);
        setMessage({ severity: 'error', text: PENDING_ERRORS[e.code] || e.message });
    };

    // The request as its task left it (`pollPending`).
    const settled = (row) => {
        if (row.status === 'applied') {
            finish('success', appliedText(pending), row);
        } else if (row.status === 'stale') {
            finish('error', PENDING_ERRORS.stale, row);
        } else if (row.status === 'pending') {
            finish('warning', 'Not applied: it is waiting again. Try again in a moment.', row);
        } else {
            const what = pending.kind === 'revert' ? 'The revert did not run' : 'Not applied';
            finish('error', `${what}: ${row.error || String(row.status || '').replace(/_/g, ' ')}`, row);
        }
    };

    const apply = async () => {
        setBusy(true);
        setMessage(null);
        try {
            let expectedDigest;
            if (pending.kind === 'revert' && pending.change_set_id) {
                // The plan the person approves is the plan as it stands now; the backend refuses (stale) when
                // it is no longer the plan the requester saw.
                const plan = await api.getRevertPlan(dataAPI, pending.change_set_id, {
                    skips: pending.revert_skips || [],
                    confirmSoftRefs: pending.revert_confirm_soft_refs,
                });
                expectedDigest = plan.digest;
            }
            const res = await api.applyPending(dataAPI, pending.id, { note: note.trim() || undefined, expectedDigest });
            const out = res.outcome || {};
            if (out.task_id && !(pending.kind === 'revert' && pending.change_set_id)) {
                setMessage({ severity: 'info', text: pending.kind === 'revert' ? 'Reverting…' : QUEUED });
                cancel.current = pollPending(dataAPI, pending.id, settled, fail, pollMs);
                return;
            }
            if (pending.kind === 'revert' && out.task_id) {
                setMessage({ severity: 'info', text: 'Reverting…' });
                cancel.current = pollRevert(
                    dataAPI,
                    pending.change_set_id,
                    out.task_id,
                    (o) => (o.status === 'reverted'
                        ? finish('success', 'Reverted.', res)
                        : finish('error', `The revert did not run: ${o.status.replace(/_/g, ' ')}. ${o.detail || ''}`, res)),
                    fail,
                    pollMs,
                );
                return;
            }
            if (out.status === 'applied' || res.status === 'applied') {
                finish('success', appliedText(pending), res);
            } else {
                finish('error', out.detail || res.error || `Not applied (${out.status || res.status}).`, res);
            }
        } catch (e) {
            fail(e);
        }
    };

    const reject = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const row = await api.rejectPending(dataAPI, pending.id, { note: note.trim() || undefined });
            finish('info', 'Rejected.', row);
        } catch (e) {
            fail(e);
        }
    };

    const open = pending.status === 'pending';
    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 1 }} data-testid={`pending-${pending.id}`}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{pending.kind === 'revert' ? 'Revert request' : 'Apply request'}</Typography>
                <StatusChip status={pending.status} />
                <ScopeChips scopes={pending.scopes} />
                <Typography variant="caption" color="text.secondary">
                    {company ? `${company} · ` : ''}{fmtTs(pending.created_ts)} · {actorLabel(pending.requested_by, pending)}
                    {pending.oauth_client_name && <>{' · via '}<ClientName name={pending.oauth_client_name} /></>}
                </Typography>
            </Stack>
            <ReasonText text={pending.reason} />
            {showSummary && <RequestSummary pending={pending} />}
            {Array.isArray(pending.scopes) && pending.scopes.includes('vocabulary')
                && typeof hasExplicitPerm === 'function'
                && VOCABULARY_GRANTS.some((action) => hasExplicitPerm('vocabulary', action)) && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                    {/* the request's company, whatever company the sidebar shows */}
                    <RouterLink to={`${VOCABULARY_PAGE_PATH}?company=${encodeURIComponent(pending.tour_operator_id)}`}>Open the AI vocabulary</RouterLink>
                </Typography>
            )}
            {pending.decided_by && (
                <Typography variant="caption" color="text.secondary" component="div">
                    Decided by {actorLabel(pending.decided_by)} at {fmtTs(pending.decided_ts)}
                    {pending.decision_note ? `: ${pending.decision_note}` : ''}
                </Typography>
            )}
            {pending.error && <Alert severity="warning" sx={{ mt: 1 }}>{pending.error}</Alert>}
            {pending.result_change_set_id && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Result: <ChangeSetLink id={pending.result_change_set_id}>change set {pending.result_change_set_id.slice(0, 8)}</ChangeSetLink>
                </Typography>
            )}
            {open && canApprove(pending) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }} alignItems={{ sm: 'center' }}>
                    <TextField size="small" label="Note (optional)" value={note} onChange={(ev) => setNote(ev.target.value)} sx={{ flex: 1 }} />
                    <Button variant="contained" disabled={busy} onClick={apply}>
                        Apply
                    </Button>
                    <Button color="error" disabled={busy} onClick={reject}>
                        Reject
                    </Button>
                    {busy && <CircularProgress size={20} />}
                </Stack>
            )}
            {open && !canApprove(pending) && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                    {waitingText(pending, hasExplicitPerm, company)}
                </Typography>
            )}
            {message && <Alert severity={message.severity} sx={{ mt: 1 }}>{message.text}</Alert>}
        </Paper>
    );
};

const REVERT_ERRORS = {
    domain_disabled: `${DOMAIN_DISABLED}: nothing can be reverted while it is off.`,
};

const OUTCOME_TEXT = {
    stale_plan: 'The records changed since this plan: here is the new plan, decide again.',
    revert_blocked: 'Some records need a decision first: here is the new plan.',
    revert_refused: 'The revert was refused',
    change_sets_disabled: 'AI changes cannot be reverted yet on this server (change sets are off).',
    revert_failed: 'The revert rolled back — nothing changed.',
};

/**
 * Revert a change set (AIE §3.5): the dry-run plan, skips on skippable records (a skip cascades — the plan
 * says which other records go with it), soft-reference confirmation, a required reason. Never forced.
 */
export const RevertDialog = ({ changeSetId, open, onClose, onDone, pollMs = POLL_MS }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [skips, setSkips] = React.useState([]);
    const [confirmSoftRefs, setConfirmSoftRefs] = React.useState(false);
    const [plan, setPlan] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [reason, setReason] = React.useState('');
    const [running, setRunning] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [replan, setReplan] = React.useState(0);
    const cancel = React.useRef(null);
    React.useEffect(() => () => cancel.current && cancel.current(), []);

    React.useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setLoading(true);
        api.getRevertPlan(dataAPI, changeSetId, { skips, confirmSoftRefs })
            .then((p) => alive && setPlan(p))
            .catch((e) => alive && setMessage({ severity: 'error', text: e.message }))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [dataAPI, changeSetId, open, skips, confirmSoftRefs, replan]);

    const toggleSkip = (seq) => setSkips((prev) => (prev.includes(seq) ? prev.filter((s) => s !== seq) : [...prev, seq].sort((a, b) => a - b)));

    const hasSoftRefs = (plan?.entries || []).some((e) => (e.issues || []).some((i) => i.code === 'soft_ref_warning'));
    const blockers = plan?.blockers || [];
    const canRun = !!plan && !loading && !running && blockers.length === 0 && reason.trim().length > 0 && plan.enabled !== false;

    const run = async () => {
        setRunning(true);
        setMessage({ severity: 'info', text: 'Reverting…' });
        try {
            const started = await api.startRevert(dataAPI, changeSetId, {
                reason: reason.trim(),
                expectedDigest: plan.digest,
                skips,
                confirmSoftRefs,
            });
            cancel.current = pollRevert(
                dataAPI,
                changeSetId,
                started.task_id,
                (outcome) => {
                    setRunning(false);
                    if (outcome.status === 'reverted') {
                        setMessage({ severity: 'success', text: 'Reverted.' });
                        if (onDone) onDone(outcome);
                        return;
                    }
                    const base = OUTCOME_TEXT[outcome.status] || outcome.status;
                    setMessage({ severity: outcome.status === 'stale_plan' || outcome.status === 'revert_blocked' ? 'warning' : 'error', text: `${base}${outcome.detail ? ` ${outcome.detail}` : ''}` });
                    if (outcome.status === 'stale_plan' || outcome.status === 'revert_blocked') {
                        setReplan((n) => n + 1);
                    }
                },
                (e) => {
                    setRunning(false);
                    setMessage({ severity: 'error', text: `Could not read the revert's outcome: ${e.message}. It may still finish — reload the page.` });
                },
                pollMs,
            );
        } catch (e) {
            setRunning(false);
            setMessage({ severity: 'error', text: REVERT_ERRORS[e.code] || e.message });
        }
    };

    return (
        <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Revert this change</DialogTitle>
            <DialogContent dividers>
                {loading && <CircularProgress size={22} />}
                {plan && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            What reverting does to each record. Records with a problem can be skipped; a skip may take
                            others with it.
                        </Typography>
                        <Table size="small" aria-label="Revert plan">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Skip</TableCell>
                                    <TableCell>Record</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {plan.entries.map((e) => (
                                    <TableRow key={e.seq}>
                                        <TableCell padding="checkbox">
                                            {e.skippable && (
                                                <Checkbox
                                                    size="small"
                                                    checked={skips.includes(e.seq)}
                                                    disabled={running}
                                                    onChange={() => toggleSkip(e.seq)}
                                                    inputProps={{ 'aria-label': `Skip record ${e.seq}` }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            #{e.seq} {e.display}
                                            {e.skipped && e.skipped !== 'requested' && (
                                                <Typography variant="caption" color="text.secondary" component="div">skipped {e.skipped}</Typography>
                                            )}
                                            {(e.issues || []).map((i, idx) => (
                                                <Typography key={idx} variant="caption" color={i.code === 'conflict' ? 'error' : 'text.secondary'} component="div">
                                                    {i.message}
                                                </Typography>
                                            ))}
                                        </TableCell>
                                        <TableCell>{e.action}</TableCell>
                                        <TableCell>{e.status}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {hasSoftRefs && (
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={<Checkbox checked={confirmSoftRefs} disabled={running} onChange={(ev) => setConfirmSoftRefs(ev.target.checked)} />}
                                label="I understand that other content still refers to records this revert changes"
                            />
                        )}
                        {blockers.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {blockers.length} record(s) need a decision before this can revert:
                                <ul style={{ margin: 0 }}>
                                    {blockers.map((b) => (
                                        <li key={`${b.seq}-${b.code}`}>#{b.seq}: {b.message}</li>
                                    ))}
                                </ul>
                                Conflicts with later edits cannot be overwritten here; skip those records, or ask Curated Planet.
                            </Alert>
                        )}
                        {plan.enabled === false && (
                            <Alert severity="info" sx={{ mt: 1 }}>AI changes cannot be reverted yet on this server (change sets are off).</Alert>
                        )}
                        <TextField
                            label="Reason"
                            required
                            fullWidth
                            multiline
                            minRows={2}
                            sx={{ mt: 2 }}
                            value={reason}
                            disabled={running}
                            onChange={(ev) => setReason(ev.target.value)}
                        />
                    </Box>
                )}
                {message && <Alert severity={message.severity} sx={{ mt: 1 }}>{message.text}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={running}>Close</Button>
                <Button variant="contained" color="error" disabled={!canRun} onClick={run}>
                    Revert
                </Button>
            </DialogActions>
        </Dialog>
    );
};
